import type {
  Excursion,
  ExcursionGroup,
  ExcursionTemplateLimitsItem,
  GroupLabels,
} from "api/excursions/Excursion.typed";
import { normalizeCustomerEmoji, rawCustomerEmojiFromApi } from "../../../common/customerEmoji";

/** Param values matching the literal type keyword render as empty in tooltips (canonical English + legacy). */
export const EMPTY_PARAM_TYPE_STRINGS = [
  "users",
  "customers",
  "guides",
  "utilisateurs",
  "clients",
  "encadrants",
];

export const EXCURSION_TOOLTIP_HOVER_OPEN_MS = 500;

export function isLabeledArray(
  v: unknown,
): v is { id: number; label: string; booking_status?: unknown }[] {
  return (
    Array.isArray(v) &&
    v.length > 0 &&
    v.every(
      (item) =>
        item &&
        typeof item === "object" &&
        "id" in item &&
        typeof (item as { id: unknown }).id === "number" &&
        "label" in item,
    )
  );
}

export function excursionTooltipHeaderCustomerEmojis(
  ex: Excursion,
  groupLabels?: GroupLabels[],
): string {
  const seen = new Set<string>();
  const parts: string[] = [];
  if (groupLabels && groupLabels.length > 0) {
    for (const gl of groupLabels) {
      for (const c of gl.customers ?? []) {
        const em = normalizeCustomerEmoji(c.emoji);
        if (em && !seen.has(em)) {
          seen.add(em);
          parts.push(em);
        }
      }
    }
    if (parts.length > 0) {
      return parts.join("\u00A0");
    }
  }
  for (const g of ex.groups ?? []) {
    for (const c of g.excursion_customers ?? []) {
      const em = normalizeCustomerEmoji(rawCustomerEmojiFromApi(c));
      if (em && !seen.has(em)) {
        seen.add(em);
        parts.push(em);
      }
    }
  }
  return parts.join("\u00A0");
}

export function formatParamValue(v: unknown): string {
  if (typeof v === "boolean") return v ? "Oui" : "Non";
  if (Array.isArray(v)) {
    if (v.length === 0) return "—";
    return v
      .map((item) =>
        item && typeof item === "object" && "label" in item ? item.label : String(item),
      )
      .join(", ");
  }
  if (v && typeof v === "object")
    return "label" in v ? String((v as { label: unknown }).label) : JSON.stringify(v);
  const s = String(v).trim();
  if (EMPTY_PARAM_TYPE_STRINGS.includes(s.toLowerCase())) return "—";

  return s || "—";
}

export function groupTitle(g: ExcursionGroup, index: number, labels?: GroupLabels): string {
  const name = labels?.user_name;
  if (name) return name;
  if ((g.excursion_users?.length ?? 0) > 0) return "Encadrants";

  return `Groupe ${index + 1}`;
}

/** Excursion-level param rows for tooltip header (raw values for picklist chips + booking_status). */
export function excursionTooltipExcursionParamEntries(
  excursion: Excursion,
  _excursionTemplatesList?: ExcursionTemplateLimitsItem[],
): [string, unknown][] {
  const raw = excursion.params;
  if (!raw || typeof raw !== "object") return [];

  const out: [string, unknown][] = [];

  for (const key of Object.keys(raw)) {
    if (key === "__types__") continue;

    const v = raw[key];
    if (v === undefined) continue;

    out.push([key, v]);
  }

  return out;
}

export const excursionCardTooltipPopperModifiers = [
  {
    name: "preventOverflow" as const,
    options: {
      boundary: "viewport" as const,
      rootBoundary: "viewport" as const,
      altBoundary: false,
      mainAxis: true,
      altAxis: true,
      padding: 8,
    },
  },
  {
    name: "flip" as const,
    options: {
      boundary: "viewport" as const,
      rootBoundary: "viewport" as const,
      altBoundary: false,
      padding: 8,
      flipVariations: true,
      fallbackPlacements: [
        "top-start",
        "top-end",
        "left",
        "right",
        "bottom",
        "bottom-start",
        "bottom-end",
      ] as const,
    },
  },
];
